import { Checkbox } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { ChecklistTask } from '../model';
import { TaskCompletionMode } from 'generated/graphql';
import { getPagePath } from '../utils/pageActionMap';
import { trackEvent } from '@client/new/services/tracking';
import { navigate } from '@client/new/helpers/history';
import * as styles from './ChecklistTaskItem.css';
import { _noop } from '@client/new/helpers/general';

type ChecklistTaskItemProps = {
  task: ChecklistTask;
  onToggle: (taskId: string, completed: boolean) => void;
  onOpenModal: (templateId: string) => void;
  influencerId: number;
};

export const ChecklistTaskItem = ({
  task,
  onToggle,
  onOpenModal,
  influencerId,
}: ChecklistTaskItemProps) => {
  const isManual = task.completionMode === TaskCompletionMode.Manual;

  const handleRowClick = () => {
    const { action } = task;
    if (action.type === 'none') return;

    trackEvent({
      type: 'Todo clicked',
      payload: {
        'Todo Title': task.title,
        'Todo Type': action.type,
      },
    });

    switch (action.type) {
      case 'modal':
        onOpenModal(action.templateId);
        break;
      case 'page': {
        const internalPath = getPagePath(action.pageKey, influencerId);
        if (internalPath) {
          navigate(internalPath);
        }
        break;
      }
      case 'external':
        window.open(action.url, '_blank');
        break;
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isManual) {
      onToggle(task.id, !task.isChecked);
    }
  };

  return (
    <div className={styles.taskItem} onClick={handleRowClick}>
      <div className={styles.taskContent}>
        <p
          className={
            task.isChecked ? styles.taskTitleCompleted : styles.taskTitle
          }
        >
          {task.title}
        </p>
        {task.subtitle && !task.isChecked && (
          <p className={styles.taskSubtitle}>{task.subtitle}</p>
        )}
        {task.estimatedTimeLabel && !task.isChecked && (
          <p className={styles.taskEstimatedTime}>
            Est. {task.estimatedTimeLabel}
          </p>
        )}
      </div>
      <div className={styles.checkboxContainer} onClick={handleCheckboxClick}>
        {isManual && (
          <Tooltip
            title={task.isChecked ? 'Mark as incomplete' : 'Mark as done'}
            variant="dark"
            placement="top"
          >
            <Checkbox
              checked={task.isChecked}
              onChange={_noop}
              sx={{
                padding: 0,
                '&.Mui-checked': {
                  color: 'rgb(95, 30, 204)',
                },
              }}
              icon={
                <FontAwesomeIcon
                  icon={faCircle}
                  className={styles.iconUnchecked}
                />
              }
              checkedIcon={
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className={styles.iconChecked}
                />
              }
            />
          </Tooltip>
        )}
        {!isManual && (
          <FontAwesomeIcon
            icon={task.isChecked ? faCircleCheck : faCircle}
            className={
              task.isChecked ? styles.iconChecked : styles.iconUnchecked
            }
          />
        )}
      </div>
    </div>
  );
};
